
import React, { useEffect, useState } from 'react';
import { ErrorBoundary } from '../common/ErrorBoundary';
import { LoadingFallback } from '../common/LoadingFallback';
import { SimpleStorageInit } from '../setup/SimpleStorageInit';
import { storageModeManager } from '@/services/storage/storageModeManager';
import { toast } from 'sonner';

interface AppInitializerProps {
  children: React.ReactNode;
}

const AppInitializer = ({ children }: AppInitializerProps) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initializationProgress, setInitializationProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('Starting...');

  useEffect(() => {
    const initialize = async () => {
      try {
        console.log('AppInitializer: Starting initialization...');
        setCurrentStep('Initializing localStorage-only storage...');
        setInitializationProgress(25);

        // Initialize storage mode manager
        await storageModeManager.initializeStorageMode();
        setInitializationProgress(50);

        setCurrentStep('Validating data integrity...');
        setInitializationProgress(75);

        setCurrentStep('Ready!');
        setInitializationProgress(100);

        console.log('AppInitializer: Initialization completed successfully');
        
        // Small delay to show completion
        setTimeout(() => {
          setIsInitialized(true);
        }, 500);

      } catch (error) {
        console.error('AppInitializer: Initialization failed:', error);
        toast.error('Application initialization failed - continuing with basic functionality');
        
        // Continue with basic functionality
        setIsInitialized(true);
      }
    };

    initialize();
  }, []);

  if (!isInitialized) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center space-y-4">
          <LoadingFallback />
          <p className="text-sm text-muted-foreground">{currentStep}</p>
          <div className="w-64 bg-gray-200 rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300" 
              style={{ width: `${initializationProgress}%` }}
            />
          </div>
          <div className="text-xs text-muted-foreground">
            🛡️ Data protection active
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <SimpleStorageInit onComplete={() => {}}>
        {children}
      </SimpleStorageInit>
    </ErrorBoundary>
  );
};

export default AppInitializer;
